import { defineStore } from "pinia";
import { api } from "@/helpers/api";

import { useUserStore } from "@/store/user";
import { getVenueSvg } from "@/helpers/svg";
import { TicketsApi } from "@xs2event/js-lib/api";

const INITIAL = {
  ticket: null,
  bookingorder: null,
  item: null,

  commission: null, // unused
  commissionPct: 5,
  sales: null,
  venueMap: Promise.resolve(""),
  venueMapSvg: "",
  eventOptions: [],
  event: null,
  eventName: null,
  eventId: null,
  categoryOptions: [],
  category: null,
  categoryId: null,
  categoryName: null,
  validFrom: null,
  validUntil: null,
  validityOptions: [], //
  ticketValidity: null,
  validity: null,
  typeOptions: [], //
  type: null,
  targetGroupOptions: [],
  targetGroup: null,
  minPrice: null,
  maxPrice: null,
  currencyCode: "EUR",
  organiserPrice: null,
  organiserPayout: null,
  ticketStatus: null,
  quantity: null,
  flags: [],
  maxQuantity: 100,
  bookingorderId: null,
  ticketId: null,
  sportType: null,
  vatCategory: "none",
  dirty: false,
};

export const useListingStore = defineStore("listing", {
  state: () => ({ ...INITIAL }),
  getters: {
    isValidQuantity() {
      return 1 <= this.quantity && this.quantity <= this.maxQuantity;
    },
    maxStockForRelist() {
      return +this.maxQuantity; // + +this.quantity  //
    },
  },
  actions: {
    async resetFormValues() {
      const eventOptions = JSON.parse(JSON.stringify(this.eventOptions)); // deep copy
      const patch = { ...INITIAL, eventOptions: eventOptions, created: null };
      this.$patch(patch);
    },
    async fetchCurrencyOptions() {
      //const req = api.cache('/tickets/sales/currencies')
      const req = api.cache("/marketplace/currencies");
      const res = await req;
      this.currencyOptions = res.data;
    },
    async fetchSales() {
      if (!this.ticket || !this.ticket.ticket_id) {
        this.sales = [];
        return;
      }
      // const req = api.get('/tickets/sales?page_size=1&ticket_id=' + this.ticket.ticket_id)
      const req = api.get("/marketplace/sales?page_size=1&ticket_id=" + this.ticket.ticket_id);
      const res = await req;

      this.sales = res.data.tickets.filter((t) => t.ticket_id === this.ticket.ticket_id);
    },
    async fetchEvent() {
      return api.cache("/events/" + this.ticket.event_id).then((res) => res.data);
    },
    async fetchVenueMap(event) {
      const { venue_id } = event || (await this.fetchEvent());

      this.venueMap = getVenueSvg(venue_id).then((svg) => {
        return svg;
      });
      this.venueMap.then((svg) => (this.venueMapSvg = svg));
    },
    async fetchSubcategoryTickets() {
      const { category_id, sub_category, ticket_targetgroup, ticket_validity } = this.ticket;
      const params = {
        category_id,
        sub_category,
        ticket_status: "available",
        //ticket_targetgroup,
        //ticket_validity,
        stock: "ge:1",
      };
      // const req = api.get('/tickets?' + new URLSearchParams(params))
      const req = api.get("/marketplace/tickets?" + new URLSearchParams(params));
      const res = await req;
      const isSameSubcategory = (ticket) =>
        ticket.ticket_targetgroup === ticket_targetgroup && ticket.ticket_validity === ticket_validity;
      return res.data.tickets.filter(isSameSubcategory);
    },
    async calculatePriceComponents() {
      const commission = (this.organiserPrice * this.commissionPct) / 100;
      const organiserPayout = Math.round((this.organiserPrice - commission) * 100) / 100;
      // VAT?
      this.$patch({
        commission,
        organiserPayout,
      });
    },
    async setTicket(ticket) {
      const patch = {
        ...INITIAL,
        ticket: ticket,
        categoryName: ticket.category_name,
        validFrom: ticket.ticket_validfrom.slice(0, 10),
        validUntil: ticket.ticket_validuntil.slice(0, 10),
        type: ticket.type_ticket,
        targetGroup: ticket.ticket_targetgroup,
        currencyCode: ticket.currency_code,
        organiserPrice: ticket.local_rates.net_rate_eur / 100,
        quantity: ticket.stock,
        flags: ticket.flags,
        maxQuantity: ticket.max_stock,
        ticketStatus: ticket.ticket_status,
        ticketValidity: ticket.ticket_validity,
        vatCategory: ticket.vat_category,
      };
      this.$patch(patch);
      await this.setEventId(ticket.event_id);
      await this.setCategoryId(ticket.category_id);
      this.calculatePriceComponents();
      this.fetchCurrencyOptions();
      this.fetchPrices();
      this.fetchSales();
      this.fetchVenueMap();
    },
    updateTicket() {
      this.ticket.local_rates.net_rate_eur = this.ticket.organiser_price = +this.organiserPrice * 100;
      this.ticket.face_value = +this.organiserPrice * 100;
      this.ticket.sales_price = +this.organiserPrice * 100;
      // const url = '/tickets/' + this.ticket.ticket_id
      const url = "/marketplace/tickets/" + this.ticket.ticket_id;
      // todo
      // url = '/marketplace/tickets/' + this.ticket.ticket_id
      return api
        .put(url, this.ticket)
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },
    saveVatCategory() {
      this.ticket.vat_category = this.vatCategory;
      this.ticket.supplier_quantity = this.ticket.stock;
      return this.updateTicket();
    },
    saveOrganiserPrice() {
      this.ticket.local_rates.net_rate_eur = this.ticket.organiser_price = +this.organiserPrice * 100;
      this.ticket.face_value = +this.organiserPrice * 100;
      this.ticket.sales_price = +this.organiserPrice * 100;
      this.ticket.currency_code = this.currencyCode;
      this.ticket.supplier_quantity = this.ticket.stock;
      return this.updateTicket();
    },
    saveStock() {
      this.ticket.local_rates.net_rate_eur = this.ticket.organiser_price = +this.organiserPrice * 100;
      this.ticket.face_value = +this.organiserPrice * 100;
      this.ticket.sales_price = +this.organiserPrice * 100;
      this.ticket.currency_code = this.currencyCode;
      this.ticket.supplier_quantity = this.ticket.stock;
      this.ticket.supplier_quantity = this.quantity;
      this.ticket.ticket_status = this.ticketStatus;
      this.ticket.flags = this.flags;
      return this.updateTicket()
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },

    // create listing

    async relistTicketId(bookingorderId, ticketId, quantity) {
      // todo:
      // api.post('/marketplace/relist', {
      //   bookingorder_id,
      //   price,
      //   quantity,
      //   ticket_id,
      // })

      // const req = api.get('/tickets/' + ticketId)
      const params = new URLSearchParams({
        show_deleted: true,
      });
      const req = api.get("/marketplace/tickets/" + ticketId + "?" + params);
      const res = await req;
      const ticket = res.data;

      const patch = {
        ...INITIAL,
        bookingorderId,
        ticketId,
        categoryName: ticket.category_name,
        validFrom: ticket.ticket_validfrom.slice(0, 10),
        validUntil: ticket.ticket_validuntil.slice(0, 10),
        ticketValidity: ticket.ticket_validity,
        type: ticket.type_ticket,
        targetGroup: ticket.ticket_targetgroup,
        currencyCode: "EUR",
        organiserPrice: ticket.local_rates.net_rate_eur / 100,
        quantity,
        maxQuantity: quantity,
        ticketStatus: ticket.ticket_status,
      };
      this.$patch(patch);
      await this.setEventId(ticket.event_id);
      await this.setCategoryId(ticket.category_id);
      this.calculatePriceComponents(this.organiserPrice);
      this.fetchCurrencyOptions();
      this.fetchPrices();
      // this.fetchSales()
      // this.fetchVenueMap()
    },
    async relistItems(bookingorder, items, maxQuantity) {
      await this.fetchEvents();
      const bookingorderId = bookingorder.bookingorder_id;
      const ticketId = items[0].ticket_id;
      this.relistTicketId(bookingorderId, ticketId, maxQuantity);
      this.relist = items;
    },
    createNewTicket() {
      this.$patch({ ...INITIAL });
    },
    async setCategoryId(categoryId) {
      const isValidCategoryId = String(categoryId).match(/^[0-9a-f]{32}/);
      if (isValidCategoryId) {
        this.categoryId = categoryId || undefined;
        const req = api.get("/categories/" + categoryId);
        const res = await req;
        const category = res.data;
        this.setCategory(category);
      } else {
        this.categoryId = undefined;
      }
    },
    async setEventId(eventId) {
      this.eventId = eventId;
      const req = api.get("/events/" + eventId);
      const res = await req;
      const event = res.data;
      this.setEvent(event);
    },
    setEvent(value) {
      this.event = value;
      this.eventName = this.event?.event_name;
      this.fetchCategories();
      this.fetchValidities();
      this.fetchCurrencyOptions();
    },
    setCategory(value) {
      this.category = value;
      this.categoryId = value?.category_id;
      this.categoryName = value?.category_name;
    },
    async fetchPrices() {
      this.minPrice = null;
      this.maxPrice = null;

      const ticketsApi = new TicketsApi();
      if (this.categoryId && this.eventId && this.ticketValidity) {
        const data = await ticketsApi.ticketsalesrecommendationGet({
          categoryId: this.categoryId,
          eventId: this.eventId,
          validity: this.ticketValidity,
        });
        this.minPrice = (data.cheapest / 100).toFixed(2);
        this.maxPrice = (data.costliest / 100).toFixed(2);
      }
    },
    setTicketValidity(validity, from, until = from) {
      const start = Date.parse(this.event.date_start + "Z");
      const offs = (n) => n * 24 * 60 * 60 * 1000;
      const day = (n) => new Date(start + offs(n)).toJSON().slice(0, 10);
      this.validFrom = day(from);
      this.validUntil = day(until);
      this.ticketValidity = validity;
    },
    setValidity(value) {
      if (value === "fri") this.setTicketValidity("singleday", 0);
      if (value === "sat") this.setTicketValidity("singleday", 1);
      if (value === "sun") this.setTicketValidity("singleday", 2);
      if (value === "sat-sun") this.setTicketValidity("twoday", 1, 2);
      if (value === "fri-sat-sun") this.setTicketValidity("weekend", 0, 2);
      this.fetchPrices(this.category.category_id);
      this.validity = value;
    },
    setType(value) {
      this.type = value;
    },
    setTargetGroup(value) {
      this.targetGroup = value;
    },
    setCurrencyCode(value) {
      this.currencyCode = value;
    },
    setOrganiserPrice(value) {
      this.organiserPrice = +value;
      this.calculatePriceComponents(value);
    },
    setTicketStatus(value) {
      this.ticketStatus = value;
    },
    setQuantity(value) {
      this.quantity = +value;
      if (this.quantity > this.maxQuantity) {
        this.quantity = this.maxQuantity;
      }
    },
    setVatCategory(value) {
      this.vatCategory = value;
    },
    generateExternalTicketId() {
      return (
        this.validFrom.slice(0, 4) +
        "-" +
        this.categoryName.replace(/\W+/g, "").toLowerCase() +
        "-" +
        Math.random().toFixed(4).slice(-4)
      );
    },
    async relistTicket() {
      const relist = {
        bookingorder_id: this.bookingorderId,
        ticket_id: this.ticketId,
        organiser_price: Math.round(+this.organiserPrice * 100),
        face_value: Math.round(+this.organiserPrice * 100),
        sales_price: Math.round(+this.organiserPrice * 100),
        supplier_quantity: +this.quantity,
        flags: this.flags,
        currency_code: this.currencyCode,
        vat_category: this.vatCategory,
      };

      const req = api.post("/marketplace/relist", relist);
      const res = await req;
      return res.data;
    },
    async createTicket() {
      const userStore = useUserStore();
      const ticket = {
        xs2event_price: Math.round(+this.organiserPrice * 100),
        organiser_price: Math.round(+this.organiserPrice * 100),

        supplier_id: userStore.supplier_id,

        // category_name: this.category.category_name,
        category_id: this.categoryId,
        category_name: this.categoryName,
        discount_percentage: 0,
        type_ticket: this.type,
        // ticket_status: this.ticketStatus,

        supplier_quantity: +this.quantity,
        backorder_quantity: 0,
        currency_code: this.currencyCode,

        ticket_status: "available",
        ticket_validfrom: this.validFrom,
        ticket_validuntil: this.validUntil,
        ticket_validity: this.ticketValidity,

        min_order: 1,
        ticket_targetgroup: this.targetGroup,
        service_fee: 0,
        vat_category: "none",
        event_id: this.event.event_id,
        external_ticket_id: this.generateExternalTicketId(), // "2023-arena1-8323"
      };

      // const req = api.post('/tickets', ticket)
      const req = api.post("/marketplace/tickets", ticket);
      const res = await req;

      return res;
    },
    async fetchEvents() {
      this.event = null;
      this.eventOptions = [];
      const params = {
        page_size: 999,
        sport_type: this.sportType ? this.sportType : "in:[formula1, motogp, motorsport]",
        date_start: "ge:" + new Date(Date.now() + 24 * 60 * 60 * 1000 * 2).toJSON().slice(0, 10),
        event_status: "notstarted",
        sorting: "event_name:asc",
      };
      const req = api.cache("/events?" + new URLSearchParams(params));
      const res = await req;
      this.eventOptions = res.data.events;
    },
    async fetchCategories() {
      this.category = null;
      this.categoryOptions = [];
      const params = {
        page_size: 999,
        event_id: this.event.event_id,
        sorting: "category_name:asc",
      };
      const req = api.cache("/categories?" + new URLSearchParams(params));
      const res = await req;
      this.categoryOptions = res.data.categories;
    },
    fetchValidities() {
      this.validity = null;
      this.validities = [
        { label: "Fri", value: "singleday" },
        { label: "Sat", value: "singleday" },
        { label: "Sun", value: "singleday" },
        { label: "Fri-Sat-Sun", value: "weekend" },
        { label: "Sat-Sun", value: "twoday" },
      ];
    },
    fetchRelist(bookingorderId) {
      return api.get(`/marketplace/relist?bookingorder_id=${bookingorderId}`).then((res) => {
        return res;
      });
    },

    deleteRelisting() {
      return api.delete(`/marketplace/tickets/${this.ticket.ticket_id}`).then((res) => {
        this.ticket = null;
        return res;
      });
    },
  },
});
